import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { AssessmentStatusEnum } from "src/app/core/models/assessment-status.enum";
import { CalibrationStatusEnum } from "src/app/core/models/calibration-status.enum";
import { CycleGradeSystemEnum } from "src/app/pages/performance-manage/models/enum";
import { CycleService } from "src/app/core/services/cycle.service";
import {
  BehaviorSubject,
  combineLatest,
  combineLatestAll,
  map,
  mergeMap,
  Subject,
  switchMap,
  tap,
} from "rxjs";
import { AssessmentQualitativeOption } from "src/app/pages/performance-manage/models/assesment-item.interface";
import { EmployeeGeneralDataAssessment } from "src/app/core/services/contributor-profile/general-data.service";
import { IGradeResult } from "src/app/pages/performance-manage/models/employee-assessment-data.interface";

@Component({
  selector: "app-performance-assessment",
  templateUrl: "./performance-assessment.component.html",
  styleUrls: ["./performance-assessment.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerformanceAssessmentComponent implements OnInit, OnChanges {
  @Input() assessment: EmployeeGeneralDataAssessment;
  @Input() hasActionButtons: boolean;
  @Input() hideAssessmentGrade: boolean | null;
  @Input() hideAssessmentQualitativeGrade: boolean | null;
  @Output() getChart = new EventEmitter();
  @Output() goToAssessmentPage = new EventEmitter();

  isFinalizedAssessment = false;
  isCalibratedAssessment = false;

  private qualitativeOptions: AssessmentQualitativeOption[] | null = null;

  qualitativeGradeScoreMessage = new BehaviorSubject<string>("N/A");

  updateQualitativeGradeScore() {
    this.qualitativeGradeScoreMessage.next(
      this.qualitativeOptions?.find(
        (x) => x.score === this.assessment?.employee?.frammedAverage,
      )?.name || "N/A",
    );
  }

  constructor(private cycleService: CycleService) {}

  ngOnInit(): void {
    if (this.assessment) {
      this.isFinalizedAssessment =
        this.assessment.employee.statusAssessmentId ===
        AssessmentStatusEnum.Finalizado;

      this.isCalibratedAssessment =
        this.assessment.employee.statusCalibrationId ===
        CalibrationStatusEnum.Calibrado;
    }

    this.cycleService
      .getQualitativeOptions()
      .pipe(map((x) => x.data))
      .subscribe((x) => {
        this.qualitativeOptions = x || null;
        this.updateQualitativeGradeScore();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const previousAssessment = changes["assessment"]
      ?.previousValue as EmployeeGeneralDataAssessment | null;
    const currentAssessment = changes["assessment"]
      ?.currentValue as EmployeeGeneralDataAssessment | null;

    if (
      previousAssessment?.employee?.frammedAverage !==
      currentAssessment?.employee?.frammedAverage
    ) {
      this.updateQualitativeGradeScore();
    }
  }

  getAssessmentChart(chartType: string) {
    this.getChart.emit(chartType);
  }

  goTo(assessmentId: string) {
    this.goToAssessmentPage.emit(assessmentId);
  }

  print(): void {
    window.print();
  }

  get _hideAssessmentGrade() {
    if (typeof this.hideAssessmentGrade === "boolean")
      return this.hideAssessmentGrade;

    if (!this.assessment) return true;

    const gradingSystem =
      this.assessment.isNinebox && this.assessment.hasNineboxChart
        ? this.assessment.nineboxChartDataSet?.currentGrade.gradingSystem
        : this.assessment.hasChart
          ? this.assessment.spiderChartDataSet?.currentGrade.gradingSystem
          : null;

    return gradingSystem === CycleGradeSystemEnum.Qualitative;
  }

  get _hideAssessmentQualitativeGrade() {
    if (typeof this.hideAssessmentQualitativeGrade === "boolean")
      return this.hideAssessmentQualitativeGrade;

    if (!this.assessment) return true;

    const gradingSystem =
      this.assessment.isNinebox && this.assessment.hasNineboxChart
        ? this.assessment.nineboxChartDataSet?.currentGrade.gradingSystem
        : this.assessment.hasChart
          ? this.assessment.spiderChartDataSet?.currentGrade.gradingSystem
          : null;

    return gradingSystem !== CycleGradeSystemEnum.Qualitative;
  }
}
